<template>
  <b-container fluid class="">
    <h1 class="m-3">
      {{ title }}
    </h1>
    <commonActions :data-ready="dataReadyTables" :filename-prefix="title" :use-time-filter="false"
      :date-dimension="dimensionEnum.SMARTTOUCHMAINTENANCE_EVENT_DATE"
      @commonFiltersUpdated="commonFiltersUpdated($event)" @clickDownloadPdf="downloadPdf($event)"
      @clickDownloadExcel="downloadExcel($event)" @clickDownloadCsv="downloadCsv($event)" />
    <table-renderer v-for="data of tablesData" :key="data.tableNumber" :table-number="data.tableNumber"
      :data-table="data.dataTable" :dimension-fields="dimensionColumnsTables" :measure-fields="measureColumnsTables"
      :table-header="data.tableHeader" :data-ready="dataReadyTables" />
    <table-renderer :key="globalData.tableNumber" :data-table="globalData.dataTable"
      :dimension-fields="dimensionColumnsGlobal" :measure-fields="measureColumnsGlobal"
      :table-header="globalData.tableHeader" :data-ready="dataReadyTables" />
  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import commonActions from "@/components/pages/commonActions";
import dimensionEnum from "@/model/dimensionEnum";
import mesureEnum from "@/model/mesureEnum";
import { columnTypeEnum } from "@/model/columnsEnums";
import { SmartTouchMaintenance } from "@/router/routeNames";
import moment from "moment";
import { dateFormatEnum } from "@/filter/dateFormat";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: SmartTouchMaintenance,
  mixins: [abortMixin],
  components: {
    TableRenderer,
    commonActions,
  },
  data() {
    return {
      commonFilters: [],
      timeDimension: null,
      siteValues: [],
      dimensionEnum: dimensionEnum,
      dataReadyTables: false,
      measureColumnsTables: [],
      dimensionColumnsTables: [],
      measureColumnsGlobal: [],
      dimensionColumnsGlobal: [],
      groupHeaders: [],
      tablesData: [],
      globalData: [],
    };
  },
  computed: {
    title() {
      return this.$t("title.maintenance");
    },
    customTableClass() {
      return "table-width-medium";
    },
  },
  mounted() {
    this.initDisplayTables();
  },
  methods: {
    commonFiltersUpdated(value) {
      this.commonFilters = value.filters;
      this.timeDimension = value.timeDimension || null;
      this.siteValues = value.siteValues;
      this.updateTables();
    },
    updateTables() {
      var dimensions = [
        dimensionEnum.SITE,
        dimensionEnum.SITE_NAME,
        dimensionEnum.SMARTTOUCH_NUMBER,
        dimensionEnum.GAME_NAME,
        dimensionEnum.GAME_CODE,
        dimensionEnum.EMPLOYEE_BEGIN_MAINTENANCE_USER,
        dimensionEnum.EMPLOYEE_END_MAINTENANCE_USER,
      ];
      this.updateTableList(this.commonFilters, dimensions);
    },
    async updateTableList(filters, dimensions) {
      try {
        const { signal } = this.resetAbortController();
        var self = this;
        self.dataReadyTables = false;

        self.tablesData = [];
        // Initialisation des objets contenant toutes les données pour chaque site :
        // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
        self.siteValues.forEach((site, i) => {
          var table = {
            tableNumber: i.toString(),
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
          };
          self.tablesData.push(table);
        });

        self.globalData = {
          tableNumber: self.tablesData.length.toString(),
          tableHeader: this.$t("Site.globalTable"),
          dataTable: [],
        };

        let siteFilter = this.commonFilters.find(f => f.dimension === dimensionEnum.SITE) || this.commonFilters[0];
        let dateRangeValues = this.timeDimension ? this.timeDimension.dateRange : [];

        var query = {
          dimensions: dimensions,
          measures: [
            mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM,
            mesureEnum.VSMARTTOUCHMAINTENANCE_LAST_EVENT_DATE,
            mesureEnum.VSMARTTOUCHMAINTENANCE_LAST_END_OF_MAINTENANCE,],
          filters: [
            siteFilter,
            {
              or: [
                {
                  and: [
                    {
                      dimension:
                        dimensionEnum.SMARTTOUCHMAINTENANCE_END_OF_MAINTENANCE,
                      operator: "equals",
                      values: [null],
                    },
                    {
                      dimension: dimensionEnum.SMARTTOUCHMAINTENANCE_EVENT_DATE,
                      operator: "beforeDate",
                      values: [moment(
                        dateRangeValues[1],
                        dateFormatEnum.SQL_DATE_FORMAT
                      ).add(1, "days")],
                    },
                  ],
                },
                {
                  dimension:
                    dimensionEnum.SMARTTOUCHMAINTENANCE_EVENT_DATE,
                  operator: "inDateRange",
                  values: dateRangeValues,
                },
                {
                  dimension:
                    dimensionEnum.SMARTTOUCHMAINTENANCE_END_OF_MAINTENANCE,
                  operator: "inDateRange",
                  values: dateRangeValues,
                },
              ],
            },
            {
              dimension: dimensionEnum.SMARTTOUCH_ISDELETED,
              operator: "equals",
              values: ["0"],
            },
          ],
        };

        var resultSets = await TableTools.Helper.doRequest(query, { signal });

        // Create data hours & days (for lisibility)
        for (var value of resultSets) {
          const splitedDate = moment.utc(value[dimensionEnum.SMARTTOUCHMAINTENANCE_LAST_EVENT_DATE]).local();
          value[dimensionEnum.SMARTTOUCHMAINTENANCE_EVENT_DAY] = splitedDate.format('YYYY/MM/DD')
          value[dimensionEnum.SMARTTOUCHMAINTENANCE_EVENT_TIME] = splitedDate.format('HH:mm')

          if (value[dimensionEnum.SMARTTOUCHMAINTENANCE_LAST_END_MAINTENANCE] !== null) {
            const splitedEndDate = moment.utc(value[dimensionEnum.SMARTTOUCHMAINTENANCE_LAST_END_MAINTENANCE]).local();
            value[dimensionEnum.SMARTTOUCHMAINTENANCE_END_MAINTENANCE_DAY] = splitedEndDate.format('YYYY/MM/DD')
            value[dimensionEnum.SMARTTOUCHMAINTENANCE_END_MAINTENANCE_TIME] = splitedEndDate.format('HH:mm')
          }
        }

        // Complétion des objets avec les données reçues pour chaque site
        self.siteValues.forEach((site) => {
          var table = self.tablesData.find(
            (tab) => tab.tableHeaderId === site.id
          );

          table.dataTable = resultSets.filter(
            (result) => result[dimensionEnum.SITE] === site.id
          );

          // Ordering data
          // First by lasteventdate
          table.dataTable.sort((smartTouchA, smartTouchB) => (new Date(smartTouchA[dimensionEnum.SMARTTOUCHMAINTENANCE_LAST_EVENT_DATE]) - new Date(smartTouchB[dimensionEnum.SMARTTOUCHMAINTENANCE_LAST_EVENT_DATE])))

          // Then by Cumulative stopped duration
          var serialNumberOrder = table.dataTable.reduce((res, curr) => {
            const existing = res.find((e) => e[dimensionEnum.SMARTTOUCH_NUMBER] === curr[dimensionEnum.SMARTTOUCH_NUMBER]);
            if (existing) {
              existing[dimensionEnum.SMARTTOUCHMAINTENANCE_MAINTENANCE_DURATION_SUM] = existing[mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM] + curr[mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM];
            } else {
              curr[dimensionEnum.SMARTTOUCHMAINTENANCE_MAINTENANCE_DURATION_SUM] = curr[mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM]
              res.push(curr);
            }

            return res;
          }, []).sort((smartTouchA, smartTouchB) => (smartTouchB[dimensionEnum.SMARTTOUCHMAINTENANCE_MAINTENANCE_DURATION_SUM] - smartTouchA[dimensionEnum.SMARTTOUCHMAINTENANCE_MAINTENANCE_DURATION_SUM])).map((elem) => elem[dimensionEnum.SMARTTOUCH_NUMBER])

          table.dataTable.sort((smartTouchA, smartTouchB) => (serialNumberOrder.indexOf(smartTouchA[dimensionEnum.SMARTTOUCH_NUMBER]) - serialNumberOrder.indexOf(smartTouchB[dimensionEnum.SMARTTOUCH_NUMBER])))
        });

        for (const serialNumber of new Set(resultSets.map((elem) => elem[dimensionEnum.SMARTTOUCH_NUMBER]))) {
          const result = resultSets.filter((result) => result[dimensionEnum.SMARTTOUCH_NUMBER] == serialNumber);
          var dataGlobal = { ...result[0] }

          dataGlobal[dimensionEnum.SMARTTOUCHMAINTENANCE_NB_STOPS] = result.length;
          dataGlobal[mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM] = result.map((elem) => elem[mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM]).reduce((sum, current) => { return sum + current }, 0);
          self.globalData.dataTable.push(dataGlobal)
        }

        // Ordering data
        self.globalData.dataTable.sort((smartTouchA, smartTouchB) => (smartTouchB[mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM] - smartTouchA[mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM]))

        self.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    /** Initialise ce qui concerne l'affichage commun à tous les tableaux de ce rapport : quelles colonnes doivent réellement être affichées, dans quel ordre
     */
    initDisplayTables() {
      const self = this;
      self.dimensionColumnsTables = TableTools.Helper.constructColumnTable(
        [
          dimensionEnum.SMARTTOUCH_NUMBER,
          dimensionEnum.GAME_CODE,
          dimensionEnum.GAME_NAME,
          dimensionEnum.EMPLOYEE_BEGIN_MAINTENANCE_USER,
          dimensionEnum.EMPLOYEE_END_MAINTENANCE_USER,
        ],
        null,
        columnTypeEnum.DIMENSION,
        false,
        null
      );

      self.dimensionColumnsGlobal = TableTools.Helper.constructColumnTable(
        [
          dimensionEnum.SMARTTOUCH_NUMBER,
          dimensionEnum.SITE_NAME,
          dimensionEnum.GAME_CODE,
          dimensionEnum.GAME_NAME,
          dimensionEnum.SMARTTOUCHMAINTENANCE_NB_STOPS],
        null,
        columnTypeEnum.DIMENSION,
        false,
        null
      );

      self.measureColumnsTables = TableTools.Helper.constructColumnTable(
        [
          dimensionEnum.SMARTTOUCHMAINTENANCE_EVENT_DAY,
          dimensionEnum.SMARTTOUCHMAINTENANCE_EVENT_TIME,
          dimensionEnum.SMARTTOUCHMAINTENANCE_END_MAINTENANCE_DAY,
          dimensionEnum.SMARTTOUCHMAINTENANCE_END_MAINTENANCE_TIME,
          mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM,
        ],
        null,
        columnTypeEnum.MEASURE,
        true,
        null
      );

      self.measureColumnsGlobal = TableTools.Helper.constructColumnTable(
        [
          mesureEnum.VSMARTTOUCHMAINTENANCE_DURATION_SUM
        ],
        null,
        columnTypeEnum.MEASURE,
        true,
        null
      );
    },
    downloadPdf(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      const self = this;
      var datasForExport = [];

      self.tablesData.forEach((table) => {
        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          self.dimensionColumnsTables,
          self.measureColumnsTables,
          self.groupHeaders,
          table.tableHeader,
          null,
          null,
          null,
          table.tableNumber,
          self.customTableClass
        );
        datasForExport.push(data);
      });

      const exportGlobalData = TableTools.Helper.assembleExportDataTable(
        self.globalData.dataTable,
        self.dimensionColumnsGlobal,
        self.measureColumnsGlobal,
        self.groupHeaders,
        self.globalData.tableHeader,
        null,
        null,
        null,
        self.globalData.tableNumber,
        self.customTableClass
      );

      datasForExport.push(exportGlobalData);

      TableTools.Helper.storeExportData(
        datasForExport,
        self.title,
        subtitles,
        filename
      );

    },
  },
};
</script>
